import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  makeStyles,
  Paper,
  Grid,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Fade,
  Zoom,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import AiSensyLogo from "../../static/AiSensyLogo.svg";
import { Link, useLocation } from "react-router-dom";
import posthog from "../../config/posthog";
import { recoverUtmParams } from "../../utils/utmStorage";
import { pushEvent } from "../../utils/dataLayer";
import { isShopifyPlansSlug } from "./shopifyPlansPixel";
import useOfferAnalyticsScripts from "../OfferCheckout/hooks/useOfferAnalyticsScripts";
import { INTEGRATION_SERVER_URL } from "../../config/config";
import SignupEmbed from "./SignupEmbed";

const ThankYouPage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const isIndianPath = location.pathname.includes("thankyou-ind");
  const isWhitelabelPartner = location.pathname.includes(
    "thankyou-whitelabel-partner",
  );
  const isPrimePartner = location.pathname.includes(
    "thankyou-prime-partner-program",
  );
  const isAdsRegion = location.pathname.includes("thankyou-ads");
  const isWebinar = location.pathname.includes("thankyou-webinar");
  // Single-page offer checkouts (region "offers"). Self-serve INR access-code
  // flow, so they get the same "Next Steps" as the Indian plans pages rather
  // than the generic "our team will reach out" copy.
  const isOfferRegion = location.pathname.includes("thankyou-offers");
  // self-serve pages get the signup form instead of the dashboard button
  // master has no US region
  const showSignup = isIndianPath || isOfferRegion;
  const demoUrl = isAdsRegion
    ? "https://calendly.com/ads-aisensy/ads-troubleshoot"
    : "https://calendly.com/d/3k8-k9q-9r3?_gl=1*6ylf0t*_gcl_au*MTA0MTIxNTY3Ni4xNzQ0ODA5NTMzLjIxMTUxMzUwMDguMTc0ODY5NTU4Ny4xNzQ4Njk1NTg2";

  // Which plans page this purchase came from. Sent by both post-payment
  // redirects in RegionalPlansPage.js
  const planSlug = new URLSearchParams(location.search).get("slug");

  /**
   * Offer thank-you pages (/thankyou-offers) use the SAME per-page tracking
   * config as their checkout page, so a page restricted to one pixel stays
   * restricted here too.
   *
   * The decision to skip the legacy stack is made from the PATH (synchronous),
   * not from the fetch — otherwise the legacy pixels would fire for a moment
   * before the config arrived. While loading we pass `{}` ("inject nothing").
   */
  const [offerTracking, setOfferTracking] = useState(null);
  // "manual" => this page mints no access code; see `fulfilment` in the
  // region config. Null until the fetch lands, so the copy stays neutral.
  const [offerFulfilment, setOfferFulfilment] = useState(null);
  useEffect(() => {
    if (!isOfferRegion || !planSlug) return;
    // planSlug is "<region>-<version>", e.g. "offers-shopify-launch-pack".
    const version = planSlug.replace(/^offers-/, "");
    let cancelled = false;
    fetch(`${INTEGRATION_SERVER_URL}/regionalplans/t1/config/offers/${version}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (cancelled) return;
        setOfferTracking(body?.data?.tracking || {});
        setOfferFulfilment(body?.data?.fulfilment || null);
      })
      .catch(() => {
        // Tracking must never break the confirmation screen.
        if (!cancelled) setOfferTracking({});
      });
    return () => {
      cancelled = true;
    };
  }, [isOfferRegion, planSlug]);

  // `{}` means "inject nothing". Non-offer thank-you pages keep their own
  // hardcoded effects below, so the hook must stay silent there — passing
  // undefined would make it add the legacy stack a second time.
  useOfferAnalyticsScripts(isOfferRegion ? offerTracking || {} : {}, {
    viewportMeta: false,
  });

  // XPay offer pages: the buyer pays on XPay's hosted page, so the Razorpay
  // success handler that sends Meta Purchase never runs. XPay returns the buyer
  // here with `xpay_intent_id`; send Purchase once that payment is confirmed
  // paid, with its real amount. eventID + a stored flag keep refreshes from
  // counting twice. Razorpay redirects never carry `xpay_intent_id`.
  // Read from the whole URL, not just a well-formed query string: our return
  // URL already has `?slug=…`, and a naive "?xpay_intent_id=" append would
  // hide it from URLSearchParams. Fallback: the id the checkout saved in this
  // tab just before redirecting to XPay.
  const xpayIntentId = (() => {
    const m = window.location.href.match(/[?&]xpay_intent_id=([^&#]+)/);
    if (m) return decodeURIComponent(m[1]);
    if (!isOfferRegion) return null;
    try {
      return window.sessionStorage.getItem("xpay-intent-id") || null;
    } catch (e) {
      return null;
    }
  })();
  useEffect(() => {
    if (!isOfferRegion || !xpayIntentId || !offerTracking) return;
    if (offerTracking.purchaseEvent === false) return;
    if (!(offerTracking.metaPixels || []).length) return;
    const sentKey = `xpay-purchase-${xpayIntentId}`;
    try {
      if (window.localStorage.getItem(sentKey)) return;
    } catch (e) {
      // Storage blocked: eventID still lets Meta drop a duplicate.
    }
    let cancelled = false;
    const check = (attempt) => {
      fetch(`${INTEGRATION_SERVER_URL}/regionalplans/t1/customer/${encodeURIComponent(xpayIntentId)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((body) => {
          if (cancelled) return;
          const data = body?.data;
          if (data?.isPaymentCompleted && window.fbq) {
            // trackSingle: report only to this page's own pixels, not to
            // pixels other scripts (GTM) initialised on the page.
            offerTracking.metaPixels.forEach((pixelId) =>
              window.fbq(
                "trackSingle",
                pixelId,
                "Purchase",
                { value: Number(data.finalAmount), currency: data.currency },
                { eventID: xpayIntentId },
              ),
            );
            try {
              window.localStorage.setItem(sentKey, "1");
              window.sessionStorage.removeItem("xpay-intent-id");
            } catch (e) {}
            return;
          }
          // The XPay success webhook can land a few seconds after the buyer.
          if (attempt < 6) setTimeout(() => check(attempt + 1), 2000);
        })
        .catch(() => {
          // Tracking must never break the confirmation screen.
        });
    };
    check(1);
    return () => {
      cancelled = true;
    };
  }, [isOfferRegion, xpayIntentId, offerTracking]);

  // Capture UTM parameters from URL
  const [utmParams, setUtmParams] = useState({});

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);

    const utmParameterNames = [
      // Standard UTM parameters
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
      "utm_id",
      // Google Ads specific parameters
      "campaign_id",
      "adgroup_id",
      "ad_id",
      "creative",
      "keyword",
      "device",
      "matchtype",
      "network",
      "placement",
      "gclid",
      "gad_source",
      "gad_campaignid",
      // Meta Ads and other tracking parameters
      "fbclid",
      "msclkid",
      "ttclid",
      "twclid",
      "li_fat_id",
    ];

    const capturedParams = {};

    utmParameterNames.forEach((param) => {
      const value = urlParams.get(param);
      if (value) {
        capturedParams[param] = value;
      }
    });

    const resolvedParams = recoverUtmParams(capturedParams);

    setUtmParams(resolvedParams);

    if (Object.keys(resolvedParams).length > 0) {
      console.log("Captured UTM parameters on ThankYou page:", resolvedParams);
    }
  }, [location.search]);

  useEffect(() => {
    // Track plan payment completion
    posthog.capture("plans_payment_completed", {
      region: isIndianPath || isOfferRegion ? "india" : "international",
      timestamp: new Date().toISOString(),
      utm_params: utmParams,
    });

    // Set page title
    document.title = "Thank You - AiSensy";

    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    // Simulate loading state
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [utmParams]);

  // Fire the purchase conversion once, using the payload handed off from the
  // payment page. Dedupe on transaction_id and clear the handoff so a refresh
  // does not re-fire.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("as_purchase");
      if (!raw) return;
      const p = JSON.parse(raw);
      const firedKey = "as_purchase_fired_" + p.transaction_id;
      if (!sessionStorage.getItem(firedKey)) {
        pushEvent("as_purchase", p);
        sessionStorage.setItem(firedKey, "1");
      }
      sessionStorage.removeItem("as_purchase");
    } catch (e) {}
  }, []);


  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress className={classes.loadingSpinner} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Container className={classes.container}>
        <Fade in={true} timeout={1000}>
          <Box className={classes.headerLogo}>
            <img
              src={AiSensyLogo}
              alt="AiSensy Logo"
              className={classes.logo}
            />
          </Box>
        </Fade>

        <Grid container justifyContent="center">
          <Grid item xs={12} md={10} lg={8}>
            <Zoom in={true} timeout={1000}>
              <Paper className={classes.thankYouCard}>
                <Box className={classes.checkIconContainer}>
                  <CheckCircleIcon className={classes.checkIcon} />
                </Box>

                <Typography variant="h4" className={classes.thankYouTitle}>
                  {isWhitelabelPartner || isPrimePartner
                    ? "Payment Successful!"
                    : isWebinar
                      ? "You're All Set!"
                      : "Thank you for choosing AiSensy!"}
                  {!isWebinar && <span className={classes.titleEmoji}> 🎉</span>}
                </Typography>

                <Typography variant="body1" className={classes.thankYouMessage}>
                  {isWhitelabelPartner
                    ? "Thank you for joining our Whitelabel Partner Program!"
                    : isPrimePartner
                      ? "Thank you for joining the Prime Plus Partner Program!"
                      : isWebinar
                        ? "Thank you for registering for the WhatsApp Masterclass"
                        : isAdsRegion
                          ? "Your Ad Credits access code is on its way to your WhatsApp and email."
                          : offerFulfilment === "manual"
                            ? "Our team will reach out to you shortly to get your account set up."
                            : isIndianPath || isOfferRegion
                              ? "You have received an access code on your phone number and email."
                              : ""}
                </Typography>

                {isWhitelabelPartner || isPrimePartner ? (
                  <>
                    <Box className={classes.nextStepsContainer}>
                      <Typography
                        variant="h6"
                        className={classes.nextStepsTitle}
                      >
                        What's Next?
                      </Typography>

                      <Box className={classes.stepsList}>
                        <Box className={classes.stepItem}>
                          <Box className={classes.stepNumber}>✓</Box>
                          <Typography
                            variant="body1"
                            className={classes.stepText}
                          >
                            Your payment has been successfully processed and
                            confirmed.
                          </Typography>
                        </Box>

                        <Box className={classes.stepItem}>
                          <Box className={classes.stepNumber}>📧</Box>
                          <Typography
                            variant="body1"
                            className={classes.stepText}
                          >
                            You will receive a confirmation email shortly with
                            all the details.
                          </Typography>
                        </Box>

                        <Box className={classes.stepItem}>
                          <Box className={classes.stepNumber}>👥</Box>
                          <Typography
                            variant="body1"
                            className={classes.stepText}
                          >
                            Our partnership team will connect with you soon to
                            help you get started.
                          </Typography>
                        </Box>

                        {isWhitelabelPartner && (
                          <>
                            <Box className={classes.stepItem}>
                              <Box className={classes.stepNumber}>💡</Box>
                              <Typography
                                variant="body1"
                                className={classes.stepText}
                                style={{ fontWeight: 600 }}
                              >
                                <strong>Payment Breakdown:</strong>
                              </Typography>
                            </Box>
                            <Box
                              className={classes.stepItem}
                              style={{
                                backgroundColor: "rgba(16, 185, 129, 0.1)",
                                padding: "16px",
                                borderRadius: "8px",
                                marginTop: "8px",
                                border: "2px solid rgba(16, 185, 129, 0.3)",
                              }}
                            >
                              <Typography
                                variant="body1"
                                className={classes.stepText}
                              >
                                <strong>✅ First Installment (PAID):</strong>{" "}
                                ₹50,000 + GST
                                <br />
                                <strong>
                                  ⏳ Second Installment (Due after onboarding):
                                </strong>{" "}
                                ₹50,000
                                <br />
                                <br />
                                <strong>💰 Total Package Value:</strong>
                                <br />• Original Price:{" "}
                                <span
                                  style={{ textDecoration: "line-through" }}
                                >
                                  ₹2,00,000
                                </span>
                                <br />
                                • Discounted Price: ₹1,00,000
                                <br />• Your Savings: ₹1,00,000 (50% OFF!)
                              </Typography>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Box>
                  </>
                ) : isWebinar ? (
                  // Reassure (payment done + link sent to both channels) in a
                  // clean three-line block so it reads calmly, not run-on.
                  <>
                    <Typography
                      variant="body1"
                      className={classes.thankYouMessage}
                      style={{ marginTop: 4 }}
                    >
                      Your payment of <strong>₹399</strong> is confirmed. We've
                      sent your webinar joining link to your{" "}
                      <strong>email and WhatsApp</strong>.
                    </Typography>
                    <Typography
                      variant="body1"
                      className={classes.thankYouMessage}
                      style={{ marginTop: 14 }}
                    >
                      Just join us live on{" "}
                      <strong>9 June 2026 · 3:00 PM IST</strong>.
                    </Typography>
                    <Typography
                      variant="body1"
                      className={classes.thankYouMessage}
                      style={{ marginTop: 14, fontWeight: 600 }}
                    >
                      See you there!
                    </Typography>
                  </>
                ) : isAdsRegion ? (
                  <>
                    <Box className={classes.nextStepsContainer}>
                      <Typography
                        variant="h6"
                        className={classes.nextStepsTitle}
                      >
                        Next Steps
                      </Typography>

                      <Box className={classes.stepsList}>
                        <Box className={classes.stepItem}>
                          <Box className={classes.stepNumber}>1</Box>
                          <Typography
                            variant="body1"
                            className={classes.stepText}
                          >
                            Log in to the AiSensy dashboard and enter your
                            access code — your Ad Credits will be added to
                            your wallet instantly.
                          </Typography>
                        </Box>

                        <Box className={classes.stepItem}>
                          <Box className={classes.stepNumber}>2</Box>
                          <Typography
                            variant="body1"
                            className={classes.stepText}
                          >
                            Schedule a quick onboarding call — we'll set up
                            your first 2 ads with you.
                          </Typography>
                        </Box>

                        <Box className={classes.stepItem}>
                          <Box className={classes.stepNumber}>3</Box>
                          <Typography
                            variant="body1"
                            className={classes.stepText}
                          >
                            Connect your Facebook Ads Manager to launch
                            Click-to-WhatsApp campaigns on Instagram &
                            Facebook.
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </>
                ) : (isIndianPath || isOfferRegion) && offerFulfilment !== "manual" ? (
                  // Manual-fulfilment offer pages (UAE XPay — no access code is
                  // minted) fall through to the same "What's Next? Our team will
                  // reach out" block the /thankyou-uae common page shows.
                  <>
                    <Box className={classes.signupIntro}>
                      <Typography className={classes.introLogin}>
                        Existing customer?{" "}
                        <a
                          href="https://www.app.aisensy.com/login"
                          className={classes.supportLink}
                        >
                          login
                        </a>
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" className={classes.nextStepsTitle}>
                      What's Next?
                    </Typography>

                    <Typography
                      variant="body1"
                      className={classes.thankYouMessage}
                    >
                      Our team will reach out to you to help you get started.
                    </Typography>
                  </>
                )}

                {showSignup ? (
                  <SignupEmbed
                    title="Create your AiSensy account"
                    subtitle=""
                    demoUrl={demoUrl}
                    campaign="checkout_thankyou"
                    flush
                  />
                ) : (
                  <>
                    <Typography variant="body1" className={classes.supportMessage}>
                      If you have any questions, please don't hesitate to contact
                      our support team at{" "}
                      <a
                        href="mailto:support@aisensy.com"
                        className={classes.supportLink}
                      >
                        support@aisensy.com
                      </a>
                    </Typography>

                    <Box className={classes.buttonContainer}>
                      <Button
                        className={classes.actionButton}
                        href="https://app.aisensy.com/"
                        target="_blank"
                        endIcon={<ArrowForwardIcon />}
                      >
                        Go to dashboard
                      </Button>

                      <Button
                        className={classes.outlineButton}
                        href={demoUrl}
                        target="_blank"
                        endIcon={<CalendarTodayIcon />}
                      >
                        {isAdsRegion ? "Schedule an Ads Setup Call" : "Book a demo"}
                      </Button>
                    </Box>
                  </>
                )}
              </Paper>
            </Zoom>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(8),
    background: `linear-gradient(135deg, #f9fcfa 0%, #f5f5f5 100%)`,
    backgroundImage: `
      url("data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9 0h2v20H9V0zm25.134.84l1.732 1-10 17.32-1.732-1 10-17.32zm-20 20l1.732 1-10 17.32-1.732-1 10-17.32zM58.16 4.134l1 1.732-17.32 10-1-1.732 17.32-10zm-40 40l1 1.732-17.32 10-1-1.732 17.32-10zM80 9v2H60V9h20zm20 69v2H0v-2h20zm79.32-55l-1 1.732-17.32-10L82 4l17.32 10zm-80 80l-1 1.732-17.32-10L2 84l17.32 10zm96.546-75.84l-1.732 1-10-17.32 1.732-1 10 17.32zm-100 100l-1.732 1-10-17.32 1.732-1 10 17.32zM38.16 24.134l1 1.732-17.32 10-1-1.732 17.32-10zM60 29v2H40v-2h20zm19.32 5l-1 1.732-17.32-10L62 24l17.32 10zm16.546 4.16l-1.732 1-10-17.32 1.732-1 10 17.32zM111 40h-2V20h2v20zm3.134.84l1.732 1-10 17.32-1.732-1 10-17.32zM40 49v2H20v-2h20zm19.32 5l-1 1.732-17.32-10L42 44l17.32 10zm16.546 4.16l-1.732 1-10-17.32 1.732-1 10 17.32zM91 60h-2V40h2v20zm3.134.84l1.732 1-10 17.32-1.732-1 10-17.32zm24.026 3.294l1 1.732-17.32 10-1-1.732 17.32-10zM39.32 74l-1 1.732-17.32-10L22 64l17.32 10zm16.546 4.16l-1.732 1-10-17.32 1.732-1 10 17.32zM71 80h-2V60h2v20zm3.134.84l1.732 1-10 17.32-1.732-1 10-17.32zm24.026 3.294l1 1.732-17.32 10-1-1.732 17.32-10zM120 89v2h-20v-2h20zm-84.134 9.16l-1.732 1-10-17.32 1.732-1 10 17.32zM51 100h-2V80h2v20zm3.134.84l1.732 1-10 17.32-1.732-1 10-17.32zm24.026 3.294l1 1.732-17.32 10-1-1.732 17.32-10zM100 109v2H80v-2h20zm19.32 5l-1 1.732-17.32-10 1-1.732 17.32 10zM31 120h-2v-20h2v20z' fill='%2304cf66' fill-opacity='0.02' fill-rule='evenodd'/%3E%3C/svg%3E"),
      radial-gradient(circle at 25% 10%, rgba(4, 207, 102, 0.05) 0%, rgba(4, 207, 102, 0) 20%),
      radial-gradient(circle at 75% 90%, rgba(4, 207, 102, 0.05) 0%, rgba(4, 207, 102, 0) 20%),
      linear-gradient(135deg, #f9fcfa 0%, #f5f5f5 100%)
    `,
    backgroundAttachment: "fixed",
    position: "relative",
    minHeight: "100vh",
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "180px",
      background: `linear-gradient(180deg, rgba(4, 207, 102, 0.08) 0%, rgba(4, 207, 102, 0) 100%)`,
      zIndex: 0,
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(4),
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f9fcfa 0%, #f5f5f5 100%)",
  },
  loadingSpinner: {
    color: "#04cf66",
  },
  container: {
    position: "relative",
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0),
    },
  },
  headerLogo: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(2),
      justifyContent: "center",
    },
  },
  logo: {
    height: 40,
    marginRight: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      height: 32,
    },
  },
  thankYouCard: {
    padding: theme.spacing(6),
    borderRadius: "16px",
    boxShadow: "none",
    textAlign: "center",
    backgroundColor: "#ffffff",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
      borderRadius: "12px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2.5, 2),
    },
  },
  checkIconContainer: {
    position: "relative",
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
    "&:after": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "120px",
      height: "120px",
      borderRadius: "50%",
      background: "rgba(4, 207, 102, 0.1)",
      zIndex: -1,
      [theme.breakpoints.down("sm")]: {
        width: "90px",
        height: "90px",
      },
    },
  },
  checkIcon: {
    fontSize: 80,
    color: "#04cf66",
    animation: "$scaleIn 0.5s ease-out",
    [theme.breakpoints.down("sm")]: {
      fontSize: 60,
    },
  },
  "@keyframes scaleIn": {
    "0%": {
      transform: "scale(0)",
      opacity: 0,
    },
    "100%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },
  thankYouTitle: {
    fontSize: "28px",
    fontWeight: 700,
    marginBottom: theme.spacing(3),
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    color: "#04cf66",
    textWrap: "balance",
    [theme.breakpoints.down("sm")]: {
      fontSize: "20px",
      marginBottom: theme.spacing(2),
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "18px",
      lineHeight: 1.3,
    },
  },
  titleEmoji: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  thankYouMessage: {
    fontSize: "16px",
    color: "#555",
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    maxWidth: "600px",
    textWrap: "balance",
    margin: "0 auto",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      marginBottom: theme.spacing(2),
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "13px",
      lineHeight: 1.5,
    },
  },
  nextStepsContainer: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(4),
    padding: theme.spacing(4),
    backgroundColor: "rgba(4, 207, 102, 0.05)",
    borderRadius: "12px",
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
      padding: theme.spacing(3),
    },
  },
  nextStepsTitle: {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: theme.spacing(3),
    color: "#333",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
      marginBottom: theme.spacing(2),
    },
  },
  stepsList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(2),
    },
  },
  stepItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(1.5),
    },
  },
  stepNumber: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "#04cf66",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      width: "24px",
      height: "24px",
      fontSize: "14px",
    },
  },
  stepText: {
    fontSize: "15px",
    color: "#555",
    textAlign: "left",
    lineHeight: 1.5,
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      lineHeight: 1.4,
    },
  },
  supportMessage: {
    fontSize: "15px",
    color: "#666",
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
  },
  supportLink: {
    color: "#04cf66",
    textDecoration: "none",
    fontWeight: 500,
    "&:hover": {
      textDecoration: "underline",
    },
  },
  // sits right under the access code line, as in the approved mockup
  signupIntro: {
    marginTop: theme.spacing(-1.5),
    marginBottom: theme.spacing(3.5),
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(-1),
      marginBottom: theme.spacing(2),
    },
  },
  introLogin: {
    fontSize: "14px",
    color: "#666",
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
    },
  },
  buttonContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(3),
    },
  },
  actionButton: {
    backgroundColor: "#04cf66",
    color: "#ffffff",
    padding: theme.spacing(1.5, 4),
    borderRadius: "30px",
    fontWeight: 600,
    textTransform: "none",
    fontSize: "16px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#03b85a",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(4, 207, 102, 0.2)",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.2, 3),
      fontSize: "14px",
      width: "100%",
    },
  },
  outlineButton: {
    backgroundColor: "transparent",
    color: "#04cf66",
    padding: theme.spacing(1.5, 4),
    borderRadius: "30px",
    fontWeight: 600,
    textTransform: "none",
    fontSize: "16px",
    border: "2px solid #04cf66",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(4, 207, 102, 0.1)",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(4, 207, 102, 0.1)",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.2, 3),
      fontSize: "14px",
      width: "100%",
      border: "1.5px solid #04cf66",
    },
  },
}));

export default ThankYouPage;
